import { useCallback, useEffect, useRef, useState } from 'react';
import type { WallMesh } from '@/core/types';
import type { MeshStats } from '@/core/mesh/panelMesh';
import { DEFAULT_VIEWPORT_OPTIONS, PanelScene, type ViewportOptions } from './scene';
import { Slider, Swatches, Toggle } from '../components/Controls';

const BACKDROP_COLOR = { dark: '#15181c', light: '#dfe4ea' } as const;

const BACKLIGHT_COLORS = [
  { value: '#ffd9a0', label: 'Warm' },
  { value: '#ffffff', label: 'Neutral' },
  { value: '#a8d8ff', label: 'Cool' },
  { value: '#8affc8', label: 'Mint' },
] as const;

interface ViewportProps {
  wall: WallMesh | null;
  theme: 'dark' | 'light';
  stats: MeshStats | null;
  building: boolean;
  error: string | null;
  segments: number;
  requestedSegments: number;
  selectedPanel: number | null;
  onSelectPanel(index: number | null): void;
}

const MATERIAL_SWATCHES = [
  { value: '#d8dee4', label: 'Plaster' },
  { value: '#e8e2d6', label: 'Bone' },
  { value: '#2b3138', label: 'Graphite' },
  { value: '#8a6a4b', label: 'Walnut' },
  { value: '#3f6b5a', label: 'Sage' },
] as const;

export function Viewport({
  wall,
  theme,
  stats,
  building,
  error,
  segments,
  requestedSegments,
  selectedPanel,
  onSelectPanel,
}: ViewportProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<PanelScene | null>(null);
  const fittedDiagonal = useRef(0);

  const [options, setOptions] = useState<ViewportOptions>(DEFAULT_VIEWPORT_OPTIONS);
  const [showSettings, setShowSettings] = useState(false);
  const [offFrame, setOffFrame] = useState(false);

  const fitView = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    scene.fitView();
    fittedDiagonal.current = scene.wallDiagonal;
  }, []);

  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;

    const scene = new PanelScene(canvasRef.current);
    sceneRef.current = scene;
    // Panning can carry the wall clean out of the viewport, and an empty canvas
    // says nothing about which way to drag back — Fit sits in the corner giving
    // no clue that it is the way out. So the scene reports when the wall has
    // gone, and the button says so. The camera stays exactly where it was put:
    // the offer is a click, not a snap.
    scene.onFramingChange = (framed) => setOffFrame(!framed);

    const container = containerRef.current;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      scene.resize(width, height);
    });
    observer.observe(container);
    scene.resize(container.clientWidth, container.clientHeight);

    return () => {
      observer.disconnect();
      scene.dispose();
      sceneRef.current = null;
    };
  }, []);

  useEffect(() => {
    const scene = sceneRef.current;
    if (scene) scene.onSelect = onSelectPanel;
  }, [onSelectPanel]);

  useEffect(() => {
    sceneRef.current?.setOptions(options);
  }, [options]);

  useEffect(() => {
    sceneRef.current?.setBackground(BACKDROP_COLOR[theme]);
  }, [theme]);

  // Turning backlighting on used to change nothing visible at all: the frames
  // sit directly behind panels of the same footprint, so from the front they
  // are hidden — correctly, because that is where they are in the room. The
  // thing that is actually worth seeing is the light, so the preview comes on
  // with it. Switching it back off afterwards sticks.
  const hadBacklight = useRef(false);
  useEffect(() => {
    const available = wall?.backlight != null;
    if (available !== hadBacklight.current) {
      hadBacklight.current = available;
      setOptions((current) => ({ ...current, backlight: available }));
    }
  }, [wall?.backlight]);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || !wall) return;

    scene.setWall(wall);

    // Refit only when the wall changes size enough that the old framing would
    // leave it tiny or overflowing. Changing the pattern must never move the
    // camera: comparing two textures depends on the view staying put.
    const diagonal = scene.wallDiagonal;
    const ratio = fittedDiagonal.current > 0 ? diagonal / fittedDiagonal.current : Infinity;
    if (ratio > 1.35 || ratio < 0.74) fitView();
  }, [wall, fitView]);

  const downscaled = wall !== null && segments < requestedSegments;
  const light = wall?.backlight;
  const lightOk =
    light != null && light.blending.ok && light.uniformity.ok && light.transmission.ok;

  return (
    <div className="viewport" ref={containerRef}>
      <canvas ref={canvasRef} className="viewport__canvas" />

      <div className="viewport__toolbar">
        <button
          type="button"
          className={`icon-button${offFrame ? ' icon-button--alert' : ''}`}
          title="Frame the whole wall"
          onClick={fitView}
        >
          {offFrame ? 'Wall is off screen · Fit' : 'Fit'}
        </button>
        <button
          type="button"
          className={`icon-button${showSettings ? ' is-active' : ''}`}
          title="Display settings"
          onClick={() => setShowSettings((open) => !open)}
        >
          Display
        </button>
        {selectedPanel !== null ? (
          <button type="button" className="icon-button" onClick={() => onSelectPanel(null)}>
            Panel {selectedPanel + 1} · clear
          </button>
        ) : null}
      </div>

      {showSettings ? (
        <div className="viewport__settings">
          <Swatches
            label="Material"
            value={options.color}
            options={MATERIAL_SWATCHES}
            onChange={(color) => setOptions((current) => ({ ...current, color }))}
          />
          <Slider
            label="Light direction"
            value={options.lightAngle}
            min={0}
            max={359}
            step={1}
            unit="°"
            help="Sweep the key light to check how the relief reads at different times of day."
            onChange={(lightAngle) => setOptions((current) => ({ ...current, lightAngle }))}
          />
          <Slider
            label="Surface finish"
            value={options.roughness}
            min={0.05}
            max={1}
            step={0.01}
            onChange={(roughness) => setOptions((current) => ({ ...current, roughness }))}
          />
          <Slider
            label="Explode"
            value={options.explode}
            min={0}
            max={0.5}
            step={0.01}
            help="Pulls the assembly apart in the preview only — sideways, and off the light boxes where there are any. The exported parts do not move."
            onChange={(explode) => setOptions((current) => ({ ...current, explode }))}
          />
          <Toggle
            label="Faceted shading"
            checked={options.flatShading}
            help="Shows every triangle. Useful for judging whether the mesh resolution is high enough."
            onChange={(flatShading) => setOptions((current) => ({ ...current, flatShading }))}
          />
          <Toggle
            label="Show panel joints"
            checked={options.showJoints}
            onChange={(showJoints) => setOptions((current) => ({ ...current, showJoints }))}
          />
          <Toggle
            label="Shadows"
            checked={options.showShadows}
            onChange={(showShadows) => setOptions((current) => ({ ...current, showShadows }))}
          />

          {wall?.backlight ? (
            <>
              <Toggle
                label="Backlight"
                checked={options.backlight}
                help="Lights the panels from behind and drops the key light, the way the wall would look with the room lights down."
                onChange={(backlight) => setOptions((current) => ({ ...current, backlight }))}
              />
              <Toggle
                label="Show light boxes"
                checked={options.showFrames}
                help="The frames that hold the strip behind the panels. Use Explode to lift the panels off them."
                onChange={(showFrames) => setOptions((current) => ({ ...current, showFrames }))}
              />
              <Toggle
                label="Show strip run"
                checked={options.showStrip}
                help="Draws the strip where it lies on the box floors, through the panels. A closed box shows nothing of its contents otherwise."
                onChange={(showStrip) => setOptions((current) => ({ ...current, showStrip }))}
              />
              {options.backlight ? (
                <>
                  <Swatches
                    label="Light colour"
                    value={options.backlightColor}
                    options={BACKLIGHT_COLORS}
                    onChange={(backlightColor) =>
                      setOptions((current) => ({ ...current, backlightColor }))
                    }
                  />
                  <Slider
                    label="Brightness"
                    value={options.backlightStrength}
                    min={0.2}
                    max={6}
                    step={0.1}
                    onChange={(backlightStrength) =>
                      setOptions((current) => ({ ...current, backlightStrength }))
                    }
                  />
                </>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      <div className="viewport__status">
        {error ? (
          <span className="status status--error">{error}</span>
        ) : (
          <>
            {wall ? (
              <span className="status">
                {wall.wall.width.toFixed(0)} × {wall.wall.height.toFixed(0)} mm
              </span>
            ) : null}
            {stats ? (
              <span className="status">
                {stats.panels} {stats.panels === 1 ? 'panel' : 'panels'} ·{' '}
                {stats.triangles.toLocaleString('en-US')} triangles · {stats.volumeCm3.toFixed(0)}{' '}
                cm³
              </span>
            ) : null}
            {wall?.backlight ? (
              <span
                className={`status${lightOk ? '' : ' status--warn'}`}
                title={
                  lightOk
                    ? 'The air gap blends the emitters, the runs light evenly and the panel passes the light.'
                    : 'The light will not read as intended — see the Panel step for what to change.'
                }
              >
                LED · {(wall.backlight.totalStripMm / 1000).toFixed(2)} m ·{' '}
                {wall.backlight.channel
                  ? `${wall.backlight.channel.depthMm.toFixed(1)} mm channel`
                  : `${wall.backlight.airGapMm} mm gap`}{' '}
                · {wall.backlight.wattsTotal.toFixed(0)} W
              </span>
            ) : null}
            {downscaled ? (
              <span
                className="status status--muted"
                title="The export always uses the full resolution."
              >
                preview at {segments} of {requestedSegments} segments
              </span>
            ) : null}
            {building ? <span className="status status--busy">Rebuilding…</span> : null}
          </>
        )}
      </div>
    </div>
  );
}
